import { useEffect, useRef } from "react";
import type { JSX } from "react";
import { Icon } from "../ui";
import { formatTimeAgo } from "../../utils/time";
import type { NeedsReplyEpisode } from "../../modules/episodes/helpers";

interface NeedsReplyPopoverProps {
  readonly items: readonly NeedsReplyEpisode[];
  /** Jump straight to this episode in Chats. */
  readonly onSelect: (episodeId: string) => void;
  readonly onClose: () => void;
}

/**
 * Dropdown for the command-bar needs-reply bell, copied from the
 * ServiceErrorPopover precedent (ul items with icon + title +
 * time-ago, plain single-line empty state, outside-mousedown close).
 * No header row — the bell already says what the dropdown is.
 * The close check measures from the ANCHOR wrapper (parentElement) so
 * a mousedown on the bell toggle itself does not close-then-reopen.
 */
// @tested-by: tst_fe_bell_pop_001..004
// @invariant: item click quick-switches to that episode; mousedown
// outside the anchor closes; empty state matches the precedent affordance
export function NeedsReplyPopover({
  items,
  onSelect,
  onClose,
}: NeedsReplyPopoverProps): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleMouseDown(e: MouseEvent): void {
      const anchor = ref.current?.parentElement ?? ref.current;
      if (anchor && !anchor.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleMouseDown);
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => { document.removeEventListener("mousedown", handleMouseDown); };
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="absolute top-full right-0 mt-1 w-[300px] max-h-[320px] overflow-y-auto
        rounded-lg border border-edge bg-surface-secondary shadow-lg z-50"
    >
      {items.length === 0 ? (
        <div className="px-3 py-3 text-[11px] text-content-tertiary">No chats need a reply</div>
      ) : (
        <ul className="m-0 p-0 list-none">
          {items.map((ep) => (
            <li key={ep.id} className="border-b border-edge/50 last:border-0">
              {/* One clean centered line (LinkedItem row pattern:
                  flex items-center gap-2) — no baseline nudges; the
                  title ellipsizes instead of pushing the time, and the
                  tabular-nums time keeps a steady right edge. */}
              <button
                type="button"
                onClick={() => { onSelect(ep.id); }}
                className="w-full h-8 flex items-center gap-2 px-3 bg-transparent border-none
                  cursor-pointer hover:bg-surface-hover/20 text-left"
              >
                <span className="flex items-center text-content-tertiary shrink-0">
                  <Icon name="message-circle" size={12} />
                </span>
                <span className="flex-1 min-w-0 text-[11px] font-medium text-content truncate">
                  {ep.title}
                </span>
                <span className="text-[10px] text-content-tertiary shrink-0 tabular-nums">
                  {formatTimeAgo(ep.activityAt)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
